import { useState, useEffect } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type {
  CityReportCollection,
  CityReportStation,
} from "./types";

interface CityReportHeaderProps {
  token: string;
  collection: CityReportCollection;
  mainStations: CityReportStation[];
}

function introDismissKey(token: string) {
  return `city_review_intro_dismissed_${token}`;
}

export function CityReportHeader({
  token,
  collection,
  mainStations,
}: CityReportHeaderProps) {
  const [introDismissed, setIntroDismissed] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    try {
      setIntroDismissed(
        localStorage.getItem(introDismissKey(token)) === "1",
      );
    } catch {
      /* noop */
    }
  }, [token]);

  const dismissIntro = () => {
    try {
      localStorage.setItem(introDismissKey(token), "1");
    } catch {
      /* noop */
    }
    setIntroDismissed(true);
  };

  const reviewedCount = mainStations.filter((s) => s.city_verdict != null).length;
  const totalForProgress = collection.hub_size ?? mainStations.length;
  const pct =
    totalForProgress > 0
      ? Math.min(100, Math.round((reviewedCount / totalForProgress) * 100))
      : 0;

  return (
    <header className="rounded-xl border bg-background p-6 space-y-4">
      <div className="flex items-start gap-4 flex-wrap">
        {collection.logo_url ? (
          <img
            src={collection.logo_url}
            alt={collection.client_name ?? "Client logo"}
            className="h-16 w-16 rounded-lg object-contain bg-muted shrink-0"
          />
        ) : null}
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">
            {collection.title}
          </h1>
          {collection.client_name && (
            <p className="text-sm text-muted-foreground mt-1">
              Prepared for {collection.client_name}
            </p>
          )}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {collection.hub_size != null && (
              <Badge variant="secondary">{collection.hub_size} stations</Badge>
            )}
            <Badge variant="outline">{mainStations.length} main</Badge>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {reviewedCount} of {totalForProgress} main stations reviewed
          </span>
          <span>{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {collection.intro && !introDismissed && (
        <div className="rounded-lg border bg-muted/40 p-4 relative">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              About this report
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setExpanded((e) => !e)}
                aria-label={expanded ? "Collapse" : "Expand"}
              >
                {expanded ? (
                  <ChevronUp className="size-4" />
                ) : (
                  <ChevronDown className="size-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={dismissIntro}
                aria-label="Dismiss"
              >
                <X className="size-4" />
              </Button>
            </div>
          </div>
          {expanded && (
            <div className="prose prose-sm dark:prose-invert max-w-none [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2">
              <Markdown remarkPlugins={[remarkGfm]}>{collection.intro}</Markdown>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
